/* eslint-disable import/no-cycle */
import {useState} from 'react';
import {observer} from 'mobx-react-lite';
import CockpitWidget from './CockpitWidget';
import {WidgetLayout} from '../../../../models/widgetLayout';
import {WidgetLayouts} from '../../../shared/widgets/WidgetLayouts';
import {DataTableWidgetConfig} from './config/dataTableWidgetConfig';
import {useMount} from '../../../../hooks/useMount';
import TransposableTable from '../../../shared/tables/TransposableTable';
import {WorkplaceCockpitRoutes} from '../../workplaceCockpit/routes';
import {EnDash} from '../../../shared/unicodeWrapper/EnDash';
import {useStore} from '../../../../hooks/useStore';

const DataTableWidget = ({
  widgetId,
  title,
  ...props
}) => {
  const store = useStore();
  const widgetConfig = new DataTableWidgetConfig({
    rootStore: store,
    identifier: DataTableWidget.Config.widgetType.identifier,
    widgetId,
  });
  widgetConfig.currentWorkplaceId = store.cockpitStore.currentWorkplaceId;

  const workplacesSource = widgetConfig.getWorkplacesSource();
  const showWorkplace = widgetConfig.getShowWorkplace();
  const linkWorkplaceTo = widgetConfig.getSettingValue('settings', 'linkWorkplaceTo');
  const linkWorkplaceInNewTab = widgetConfig.getSettingValue('settings', 'linkWorkplaceInNewTab');
  const [workplaces, setWorkplaces] = useState([]);
  const orientation = widgetConfig.getSettingValue('settings', 'orientation');
  const fieldSettings = widgetConfig.getSettingValue('dataSource', 'fields') || [];
  const selectedHierarchies = widgetConfig.setSelectedWorkplaces();

  useMount(() => {
    const workplacesToDisplay = [];
    const selectedWorkplaces = selectedHierarchies.map(
      (hierarchyId) => store.workplaceStore.getByHierarchyId(hierarchyId)
    ).filter((wp) => !!wp);

    if (store.cockpitStore.currentWorkplaceId) {
      const currentWorkplace = store.workplaceStore.getById(store.cockpitStore.currentWorkplaceId);
      const source = widgetConfig.getWorkplacesSource();

      switch (source) {
        case DataTableWidgetConfig.SOURCE_HIERARCHY:
          workplacesToDisplay.push(currentWorkplace);
          break;
        case DataTableWidgetConfig.SOURCE_GROUP:
          if (currentWorkplace && currentWorkplace.groupId) {
            const workplaceGroup = store.workplaceGroupStore.getById(currentWorkplace.groupId);
            if (workplaceGroup) {
              workplacesToDisplay.push(...workplaceGroup.workplaces);
            }
          } else {
            workplacesToDisplay.push(currentWorkplace);
          }
          break;
        default:
          workplacesToDisplay.push(...selectedWorkplaces);
          break;
      }
    } else {
      workplacesToDisplay.push(...selectedWorkplaces);
    }

    const modelOptions = new Set();
    const sensors = new Set();
    // get the set of required model options
    fieldSettings.forEach((field) => {
      const modelOption = widgetConfig.modelOptions.find((model) => model.key === field.model);
      if (modelOption.loadAction) {
        modelOptions.add(modelOption);
      }
      if (field.model === 'sensor') {
        sensors.add(field.property);
      }
    });
    // load all dependencies per workplace and model option
    workplacesToDisplay.forEach((workplace) => {
      const loadActions = new Set();
      modelOptions.forEach((modelOption) => {
        loadActions.add(modelOption.loadAction);
      });
      loadActions.forEach((loadAction) => {
        widgetConfig[loadAction](workplace.id);
      });
      sensors.forEach((sensor) => {
        store.sensorDataStore.loadLatestByWorkplace(sensor, workplace.id);
      });
    });
    setWorkplaces(workplacesToDisplay);
  }, [
    store.cockpitStore.currentWorkplaceId,
    store.workplaceGroupStore.workplaceGroups.length,
    workplacesSource,
    selectedHierarchies.length,
  ]);

  const columns = fieldSettings.map((field) => ({
    title: field.label,
    key: `${field.model}-${field.property}`,
    dataIndex: `${field.model}-${field.property}`,
  }));

  const dataSource = workplaces.map((workplace, wpIndex) => {
    const data = {};
    if (showWorkplace) {
      data.workplaceId = workplace.id;
      data.key = workplace?.id;
    }
    fieldSettings.forEach((field, fieldIndex) => {
      const modelOption = widgetConfig.modelOptions.find((model) => model.key === field.model);
      const record = modelOption.getRecord({workplaceId: workplace.id});
      const value = record?.displayableProperties.find((prop) => prop.key === field.property)?.render(null, record);

      if (value) {
        data[`${field.model}-${field.property}`] = value.children;
      }

      if (field.model === 'sensor') {
        const sensor = store.sensorStore.getById(field.property);
        const sensorData = store.sensorDataStore.getLatestByWorkplace(field.property, workplace.id);
        if (sensorData?.value) {
          data[`${field.model}-${field.property}`] = `${sensorData?.value} ${sensor.unitOfMeasure?.label || ''}`;
        } else {
          data[`${field.model}-${field.property}`] = EnDash();
        }
      }
      data.rowId = `${wpIndex}-${fieldIndex}`;
    });
    return data;
  });

  const renderWorkplaceLink = (workplace) => {
    const label = workplace?.label;
    let link;
    if (linkWorkplaceTo === DataTableWidgetConfig.LINK_WORKPLACE_TO_COCKPIT) {
      link = `${window.location.origin}${WorkplaceCockpitRoutes.sharedCockpit(workplace?.no)}`;
    } else if (linkWorkplaceTo === DataTableWidgetConfig.LINK_WORKPLACE_TO_PRODUCTION) {
      link = `${window.location.origin}/production/${workplace?.no}`;
    }
    if (linkWorkplaceTo === DataTableWidgetConfig.LINK_WORKPLACE_NOWHERE) {
      return label;
    }
    return (
      <a href={link} target={linkWorkplaceInNewTab ? '_blank' : '_self'} rel="noopener noreferrer">
        {label}
      </a>
    );
  };

  return (
    <CockpitWidget
      widgetId={widgetId}
      title={title}
      widgetConfig={widgetConfig}
      {...props}
    >
      <TransposableTable
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        vertical={orientation === 'vertical'}
        rowKey={'rowId'}
        headerKey={showWorkplace ? 'workplaceId' : null}
        renderHeader={(id) => {
          const workplace = store.workplaceStore.getById(id);
          return (
            <span>
              {renderWorkplaceLink(workplace)}
            </span>
          );
        }}
      />
    </CockpitWidget>
  );
};

DataTableWidget.Config = DataTableWidgetConfig;
DataTableWidget.defaultLayout = new WidgetLayout({
  identifier: CockpitWidget.identifier,
  width: 4,
  height: 5,
  minHeight: 3,
  minWidth: WidgetLayouts.sixthWidth.minW,
});
export default observer(DataTableWidget);
